// Note: crypto module is not available in browser environment
// We'll use Web Crypto API instead for browser compatibility

/**
 * Hash text (username or password) with salt using PBKDF2
 * @param {string} text - Plain text to hash
 * @param {string} salt - Salt string
 * @returns {Promise<string>} - Hashed text
 */
export async function hashPassword(text: string, salt: string): Promise<string> {
  const iterations = 100000; // PBKDF2 iterations
  const keyLength = 64; // Key length in bytes
  
  // Convert text and salt to ArrayBuffer
  const encoder = new TextEncoder();
  const textBuffer = encoder.encode(text);
  const saltBuffer = encoder.encode(salt);
  
  // Import the password as a key
  const key = await crypto.subtle.importKey(
    'raw',
    textBuffer,
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );
  
  // Derive the key using PBKDF2
  const derivedKey = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: iterations,
      hash: 'SHA-512'
    },
    key,
    keyLength * 8 // Convert bytes to bits
  );
  
  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(derivedKey));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash username with salt using PBKDF2 (alias for hashPassword)
 * @param {string} username - Plain text username
 * @param {string} salt - Salt string
 * @returns {Promise<string>} - Hashed username
 */
export async function hashUsername(username: string, salt: string): Promise<string> {
  return hashPassword(username, salt);
}

/**
 * Verify password against stored hash
 * @param {string} password - Plain text password to verify
 * @param {string} salt - Stored salt
 * @param {string} storedHash - Stored password hash
 * @returns {Promise<boolean>} - True if password is valid
 */
export async function verifyPassword(password: string, salt: string, storedHash: string): Promise<boolean> {
  const hash = await hashPassword(password, salt);
  return hash === storedHash;
}

/**
 * Verify username against stored hash
 * @param {string} username - Plain text username to verify
 * @param {string} salt - Stored salt
 * @param {string} storedHash - Stored username hash
 * @returns {Promise<boolean>} - True if username is valid
 */
export async function verifyUsername(username: string, salt: string, storedHash: string): Promise<boolean> {
  const hash = await hashUsername(username, salt);
  return hash === storedHash;
}

/**
 * Authenticate user with username and password
 * @param {string} username - Plain text username
 * @param {string} password - Plain text password
 * @param {Object} userData - User data from database with username, password, and salt
 * @returns {Promise<boolean>} - True if both username and password are valid
 */
export async function authenticateUser(
  username: string, 
  password: string, 
  userData: { username: string; password: string; salt: string }
): Promise<boolean> {
  const usernameValid = await verifyUsername(username, userData.salt, userData.username);
  const passwordValid = await verifyPassword(password, userData.salt, userData.password);
  return usernameValid && passwordValid;
}
